;(() => {
  if (window.__COMPONENT_TOAST__) return
  window.__COMPONENT_TOAST__ = true

  class ComponentToast extends HTMLElement {
    static targetStates = new WeakMap()

    constructor() {
      super()
      this.hideTimer = null
      this.removeTimer = null
      this.restoreTarget = null
      this.showTransform = ''
      this.toastDuration = 3000
    }

    connectedCallback() {
      this.open()
    }

    disconnectedCallback() {
      clearTimeout(this.hideTimer)
      clearTimeout(this.removeTimer)
      this.restoreTarget?.()
      this.restoreTarget = null
    }

    configure(options) {
      const { message, type, theme, position, duration, targetNode } = options

      this.toastDuration = duration
      this.restoreTarget = this.prepareTarget(targetNode)
      this.setAttribute('data-type', type)
      this.setAttribute('data-position', position)
      this.classList.toggle('light', theme === 'light')
      this.classList.toggle('dark', theme === 'dark')
      this.applyPosition(position, targetNode)

      const messageEl = this.querySelector('.component-message__text')
      if (messageEl) messageEl.textContent = message
    }

    applyPosition(position, targetNode) {
      const { styles, hiddenTransform, showTransform } = this.getPlacement(position, targetNode)

      this.style.position = targetNode ? 'absolute' : 'fixed'
      this.style.top = styles.top
      this.style.right = styles.right
      this.style.bottom = styles.bottom
      this.style.left = styles.left
      this.style.transform = hiddenTransform
      this.showTransform = showTransform
    }

    open() {
      clearTimeout(this.hideTimer)
      clearTimeout(this.removeTimer)

      requestAnimationFrame(() => {
        this.style.transform = this.showTransform
        this.classList.add('show')
      })

      if (this.toastDuration > 0) {
        this.hideTimer = setTimeout(() => this.hide(), this.toastDuration)
      }
    }

    hide() {
      clearTimeout(this.hideTimer)
      clearTimeout(this.removeTimer)
      this.classList.remove('show')
      this.removeTimer = setTimeout(() => this.remove(), 250)
    }

    static create(options) {
      if (!options.message) return null
      const config = {
        message: options.message,
        type: options.type || 'info',
        theme: options.theme || 'dark',
        position: options.position || 'top',
        duration: options.duration ?? 3000,
        targetNode: options.target || null
      }

      const toast = new ComponentToast()
      toast.className = 'component-message'
      toast.setAttribute('role', 'alert')
      toast.setAttribute('aria-live', 'polite')
      toast.setAttribute('aria-atomic', 'true')
      const content = document.querySelector('component-message .component-message__content')
      if (content) toast.innerHTML = content.outerHTML
      else toast.innerHTML = "<div class='component-message__content'><div class='component-message__text'></div></div>"
      toast.configure(config)

      const root = config.targetNode || document.body
      root.appendChild(toast)
      return toast
    }

    prepareTarget(targetNode) {
      if (!targetNode || targetNode === document.body || targetNode === document.documentElement) return null

      const saved = ComponentToast.targetStates.get(targetNode)
      if (saved) {
        saved.count += 1
        return () => this.releaseTarget(targetNode)
      }

      const state = {
        count: 1,
        inlinePosition: targetNode.style.position,
        shouldRestorePosition: window.getComputedStyle(targetNode).position === 'static'
      }
      if (state.shouldRestorePosition) targetNode.style.position = 'relative'
      ComponentToast.targetStates.set(targetNode, state)
      return () => this.releaseTarget(targetNode)
    }

    releaseTarget(targetNode) {
      const state = ComponentToast.targetStates.get(targetNode)
      if (!state) return

      state.count -= 1
      if (state.count > 0) return

      if (state.shouldRestorePosition) targetNode.style.position = state.inlinePosition
      ComponentToast.targetStates.delete(targetNode)
    }

    getPlacement(position, targetNode) {
      const gap = 16
      const center = '50%'

      if (targetNode) {
        switch (position) {
          case 'bottom':
            return {
              styles: { top: `calc(100% + ${gap}px)`, right: 'auto', bottom: 'auto', left: center },
              hiddenTransform: 'translateX(-50%) translateY(8px)',
              showTransform: 'translateX(-50%) translateY(0)'
            }
          case 'left':
            return {
              styles: { top: center, right: 'auto', bottom: 'auto', left: `-${gap}px` },
              hiddenTransform: 'translateX(calc(-100% - 8px)) translateY(-50%)',
              showTransform: 'translateX(-100%) translateY(-50%)'
            }
          case 'right':
            return {
              styles: { top: center, right: 'auto', bottom: 'auto', left: `calc(100% + ${gap}px)` },
              hiddenTransform: 'translateX(8px) translateY(-50%)',
              showTransform: 'translateX(0) translateY(-50%)'
            }
          default:
            return {
              styles: { top: `-${gap}px`, right: 'auto', bottom: 'auto', left: center },
              hiddenTransform: 'translateX(-50%) translateY(calc(-100% - 8px))',
              showTransform: 'translateX(-50%) translateY(-100%)'
            }
        }
      }

      switch (position) {
        case 'bottom':
          return {
            styles: { top: 'auto', right: 'auto', bottom: `${gap}px`, left: center },
            hiddenTransform: 'translateX(-50%) translateY(8px)',
            showTransform: 'translateX(-50%) translateY(0)'
          }
        case 'left':
          return {
            styles: { top: center, right: 'auto', bottom: 'auto', left: `${gap}px` },
            hiddenTransform: 'translateX(-8px) translateY(-50%)',
            showTransform: 'translateX(0) translateY(-50%)'
          }
        case 'right':
          return {
            styles: { top: center, right: `${gap}px`, bottom: 'auto', left: 'auto' },
            hiddenTransform: 'translateX(8px) translateY(-50%)',
            showTransform: 'translateX(0) translateY(-50%)'
          }
        default:
          return {
            styles: { top: '130px', right: 'auto', bottom: 'auto', left: center },
            hiddenTransform: 'translateX(-50%) translateY(-8px)',
            showTransform: 'translateX(-50%) translateY(0)'
          }
      }
    }
  }

  if (!customElements.get('component-toast')) {
    customElements.define('component-toast', ComponentToast)
  }

  window.showToast = options => ComponentToast.create(options)

  document.addEventListener('toast:show', event => {
    window.showToast(event.detail || {})
  })
})()
